<template>
  <div>
    <!-- Consent gate -->
     <div v-if="isFrontPage">
        <div class="iframe-wrapper">
          <iframe
            id="calculator-iframe"
            ref="calcIframe"
            :key="srcUrl"
            :src="srcUrl"
            allow="clipboard-write"
            scrolling="no"
            frameborder="0"
            style="width: 1px; min-width: 100%; border: none;"
          />
        </div>
     </div>
     <div v-else>
        <BaseCard v-if="!isAccepted && !fetching">
          <h2 class="bold_text">
            {{ $t('subpage_rettindi_lifeyrisgatt_samthykki_og_fyrivari_header') }}
          </h2>
          <p>{{ $t('subpage_rettindi_lifeyrisgatt_samthykki_og_fyrivari') }}</p>
    
          <div class="m-b-30 consent-group">
            <label class="input-txt">
              <input
                type="radio"
                name="lifeyrisgatt_consent"
                value="yes"
                v-model="consentAnswer"
              />
              {{ $t('lan_umsokn_step7_confirm') }}
            </label>
    
            <label class="input-txt">
              <input
                type="radio"
                name="lifeyrisgatt_consent"
                value="no"
                v-model="consentAnswer"
              />
              {{ $t('umsokn_PEP_no') || 'Nei' }}
            </label>
          </div>
    
          <button
            :disabled="consenting"
            class="forms__button forms__button__forward "
            @click="acceptAndLoad"
          >
            {{ consenting ? $t('sed_gogn') : $t('lan_umsokn_afram') }}
          </button>
        </BaseCard>
    
        <!-- Loader only while network is in-flight -->
        <div v-else-if="fetching" class="loader-container">
          <v-progress-circular indeterminate color="primary" size="48" />
        </div>
    
        <!-- Calculator -->
        <div class="iframe-wrapper" v-else>
          <iframe
            id="calculator-iframe"
            ref="calcIframe"
            :key="srcUrl"
            :src="srcUrl"
            allow="clipboard-write"
            scrolling="no"
            frameborder="0"
            style="width: 1px; min-width: 100%; border: none;"
          />
        </div>
     </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import iFrameResize from 'iframe-resizer/js/iframeResizer'
import { useAuthStore } from '@/stores/useAuthStore'
import { useJoakimStore } from '@/stores/useJoakimStore'
import BaseCard from '../shared/BaseCard.vue'

defineProps<{
  isFrontPage?: boolean
}>()

type RettindiRow = { aldur: number; rettindi: number }

const { locale } = useI18n()
const auth = useAuthStore()
const joakim = useJoakimStore()
const { authUser } = storeToRefs(auth)
const { reiknudrettindi, lifeyrisgattRows } = storeToRefs(joakim) as {
  reiknudrettindi: Ref<RettindiRow[] | null>,
  lifeyrisgattRows: Ref<any[]>
}

const darkMode = ref(false)
const consentAnswer = ref<'yes' | 'no' | ''>('')  // radio
const consenting = ref(false)
const isAccepted = ref(localStorage.getItem('lifeyrisgatt') === 'true')

// fetching flags
const fetching = ref(false)
const rettindiLoaded = ref(false)

let bodyObserver: MutationObserver | null = null

function formatBirthdayFromKennitala(kennitala: string): string {
  const dd = kennitala.slice(0, 2)
  const mm = kennitala.slice(2, 4)
  const yy = parseInt(kennitala.slice(4, 6), 10)
  const currentYY = parseInt(moment().format('YY'), 10)
  const fullYear = yy <= currentYY ? 2000 + yy : 1900 + yy
  return `${fullYear}-${mm}-${dd}`
}

const birthday = computed<string | null>(() => {
  const kt = authUser.value?.username
  return kt ? formatBirthdayFromKennitala(kt) : null
})

const targetAge = computed<67 | 70 | null>(() => {
  if (!birthday.value) return null
  const age = moment().diff(moment(birthday.value, 'YYYY-MM-DD'), 'years')
  return (age <= 67 ? 67 : 70) as 67 | 70
})

function getPensionEntitlement(): number | null {
  if (!reiknudrettindi.value || !targetAge.value) return null
  const list = reiknudrettindi.value as RettindiRow[]
  const row = list.find(r => r.aldur === targetAge.value)
  return row?.rettindi ?? null
}

/** Sum of other funds (tegund === 1) at target age, excluding Gildi's own fund. */
const otherMonthlyPension = computed<number | null>(() => {
  if (!isAccepted.value) return null
  if (!targetAge.value) return 0

  const list = lifeyrisgattRows.value
  if (!Array.isArray(list) || list.length === 0) return 0

  let sum = 0
  for (const item of list) {
    if (item?.tegund !== 1) continue
    // Exclude Gildi's own fund - it's already included in pensionEntitlement
    if (item?.adili === 'Gildi - lífeyrissjóður') continue
    const row = item?.sjodFelagi?.rettindi?.manadargreidsla
      ?.find((x: any) => x.aldur === targetAge.value)
    if (row && typeof row.einingar === 'number'){
      sum += row.einingar
    } 
  }
  return sum
})

const srcUrl = computed<string>(() => {
  // Render only after base entitlement is loaded
  if (!rettindiLoaded.value) return ''

  const lang = locale.value === 'en' ? 'en' : 'is'
  const base = `https://reiknivel-lifeyrir.gildi.is/${lang}`
  const parts: string[] = []

  parts.push(`darkMode=${darkMode.value ? '1' : '0'}`)
  if (birthday.value) parts.push(`birthday=${encodeURIComponent(birthday.value)}`)

  const entitlement = getPensionEntitlement()
  if (entitlement != null) {
    parts.push(`pensionEntitlement=${encodeURIComponent(String(Math.round(entitlement)))}`)
  }

  // For accepted users always include otherMonthlyPension (0 until data arrives)
  if (otherMonthlyPension.value != null) {
    parts.push(`otherMonthlyPension=${encodeURIComponent(String(Math.round(otherMonthlyPension.value)))}`)
  }

  return `${base}?${parts.join('&')}`
})

async function initResizer() {
  await nextTick()
  iFrameResize(
    {
      log: false,
      heightCalculationMethod: 'taggedElement',
      checkOrigin: ['https://reiknivel-lifeyrir.gildi.is'],
      autoResize: true,
      resizeFrom: 'child',
    },
    '#calculator-iframe'
  )
}

async function acceptAndLoad() {
  if (consentAnswer.value !== 'yes' || consenting.value) return
  consenting.value = true
  try {
    localStorage.setItem('lifeyrisgatt', 'true')
    isAccepted.value = true

    fetching.value = true
    await auth.getAuthUser()
    await joakim.getReiknudRettindi()
    rettindiLoaded.value = true

    // Call store action; it will populate lifeyrisgattRows (array)
    await joakim.getLifeyrisgatt()
  } finally {
    fetching.value = false
    consenting.value = false
  }
}

onMounted(async () => {
  if (!authUser.value?.username) await auth.getAuthUser()

  darkMode.value = document.body.classList.contains('dark-mode')
  bodyObserver = new MutationObserver(() => {
    darkMode.value = document.body.classList.contains('dark-mode')
  })
  bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] })

  fetching.value = true
  try {
    await joakim.getReiknudRettindi()
    rettindiLoaded.value = true

    if (isAccepted.value) {
      // Populate lifeyrisgattRows if already accepted
      await joakim.getLifeyrisgatt()
    }
  } finally {
    fetching.value = false
  }
})

watch(srcUrl, (newUrl) => {
  if (newUrl) initResizer()
})

onBeforeUnmount(() => {
  bodyObserver?.disconnect()
  bodyObserver = null
})
</script>

<style scoped lang="scss">
.iframe-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
  min-height: 2400px;
  iframe { min-height: 3060px; }
}
.loader-container {
  min-height: 1200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.consent-group {
  display: flex;
  gap: 24px;
  align-items: center;
}
.input-txt {
  color: #194458;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
}
.input-txt input[type="radio"] { margin-right: 8px; }

.forms__button {
    background-color: #27445d;
    color: #fff;
    padding: 10px 16px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    line-height: 1;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background-color .2s, color .2s, border-color .2s;
    border: none;
    cursor: pointer;
    margin-top: 10px;
}


</style>
